import React, { useEffect, useRef, useState } from 'react';
import { useParamsContext } from '../../../contexts/ParamsContext';
import { PlanosService } from './Service';
import { useAppContext } from '../../../contexts/AppContext';
import { usePlanosContext } from './Context';
import { useEstabsContext } from '../../../contexts/EstabsContext';
import { ServicosService } from '../../servicos/Service';
import PriceInput from '../../../components/inputs/Price';
import { Modal } from '../../../components/Modal';
import { SettingsRow, SettingsSection } from '../../configuracoes/estabelecimento/Form/SettingsGroup';
import { formatPlanPrice, getPlanBillingLabel, getPlanPriceSuffix } from '../cliente/planosUtils';
import FormPlanoImages from './FormPlanoImagesProps';
import '../Planos.css';
import { Layers, Plus, Trash2 } from 'lucide-react';

const service = new PlanosService();
const servicosService = new ServicosService();

// Mesmo tipo que o back (PlanPeriodicityEnum)
type PlanPeriodicity =
    | 'single'
    | 'weekly'
    | 'biweekly'
    | 'monthly'
    | 'bimonthly'
    | 'quarterly'
    | 'biannual'
    | 'yearly';

type BillingType = 'RECORRENTE' | 'UNICO';
type ServiceRecurrence = 'semanal' | 'mensal';

// 🔹 Unidade da validade do plano de pagamento único
type SingleValidityUnit = 'day' | 'month';

interface PlanoServiceItem {
    serviceId: string;
    recurrence: ServiceRecurrence;
    quantity: number;
}

const normalizeServiceRecurrence = (value?: string): ServiceRecurrence =>
    value === 'semanal' ? 'semanal' : 'mensal';

const parsePercentInput = (value?: string | number) => {
    if (value === undefined || value === null) return Number.NaN;
    const normalized = String(value).replace(',', '.').trim();
    if (!normalized) return Number.NaN;
    return Number(normalized);
};

const parseBooleanFlag = (value: unknown) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    return false;
};

export default function PlanosForm() {
    const { clear, id } = useParamsContext();
    const { data, loading, reloadData } = usePlanosContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const { estabSession } = useEstabsContext();
    const doc: any = data?.find((e) => e._id === id);

    const [disabledButton, setDisabledButton] = useState(false);
    const [uploadingImages, setUploadingImages] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [agendamentoDescontoPercentualStr, setAgendamentoDescontoPercentualStr] =
        useState('100');
    const [isActive, setIsActive] = useState(true);

    // Cliente pode contratar sozinho?
    const [allowSelfSubscription, setAllowSelfSubscription] = useState(true);

    // Periodicidade do plano
    const [periodicity, setPeriodicity] =
        useState<PlanPeriodicity>('monthly');
    const isSingle = periodicity === 'single';

    // Quantidade de cobranças
    const [quantity, setQuantity] = useState<number | ''>(12);
    const [indeterminateQuantity, setIndeterminateQuantity] =
        useState(false);

    // 🔹 Validade padrão para planos de pagamento único
    const [singleValidityValue, setSingleValidityValue] =
        useState<number | ''>(1);
    const [singleValidityUnit, setSingleValidityUnit] =
        useState<SingleValidityUnit>('month');

    // Imagens do plano
    const [imagesIds, setImagesIds] = useState<any[]>([]);

    // Serviços
    const [servicesOptions, setServicesOptions] = useState<
        { label: string; value: string }[]
    >([]);
    const [services, setServices] = useState<PlanoServiceItem[]>([
        { serviceId: '', recurrence: 'mensal', quantity: 1 },
    ]);
    const allowAgendamentoDiscountField = parseBooleanFlag(
        estabSession?.enablePlanoAgendamentoDiscountField,
    );

    // Preenche uma unica vez: recarregar a lista nao pode apagar o que ja foi digitado.
    const filledDocIdRef = useRef('');

    useEffect(() => {
        if (!doc || filledDocIdRef.current === doc._id) return;
        filledDocIdRef.current = doc._id;

        setName(doc.name || '');
        setDescription(doc.description || '');
        setPrice(doc.price?.toString?.() || '');
        setAgendamentoDescontoPercentualStr(
            doc.agendamentoDescontoPercentual !== undefined &&
            doc.agendamentoDescontoPercentual !== null
                ? String(doc.agendamentoDescontoPercentual)
                : '0',
        );
        setIsActive(doc.status === 1 || doc.status === 'ATIVO');

        // periodicidade / quantidade / indeterminada
        setPeriodicity((doc.periodicity as PlanPeriodicity) || 'monthly');
        setIndeterminateQuantity(!!doc.indeterminateQuantity);
        setQuantity(typeof doc.quantity === 'number' ? doc.quantity : 12);

        // 🔹 carrega validade do plano de pagamento único (se existir no doc)
        setSingleValidityValue(
            typeof doc.singleValidityValue === 'number' ? doc.singleValidityValue : 1,
        );
        setSingleValidityUnit((doc.singleValidityUnit as SingleValidityUnit) || 'month');

        if (Array.isArray(doc.services) && doc.services.length) {
            setServices(
                doc.services.map((s: any) => ({
                    serviceId: s.serviceId || '',
                    recurrence: normalizeServiceRecurrence(s.recurrence),
                    quantity: Number(s.quantity) || 1,
                })),
            );
        }

        // Imagens existentes do plano
        setImagesIds(Array.isArray(doc.imagesIds) ? doc.imagesIds : []);

        // allowSelfSubscription (default true se não vier)
        setAllowSelfSubscription(
            typeof doc.allowSelfSubscription === 'boolean' ? doc.allowSelfSubscription : true,
        );
    }, [doc]);

    useEffect(() => {
        servicosService.findAll().then(({ data }) => {
            const activeServices = (data || []).filter(
                (s: any) => s?.status === 1 && !s?.deletedAt,
            );

            setServicesOptions(
                activeServices.map(({ _id, name }: any) => ({
                    label: name,
                    value: _id,
                })),
            );
        });
    }, []);

    // Servico ja incluido que foi desativado continua aparecendo, sem sumir em silencio do select.
    const allServiceOptions = [
        ...servicesOptions,
        ...((doc?.services || []) as any[])
            .filter((s) => s.serviceId && !servicesOptions.some((option) => option.value === s.serviceId))
            .map((s) => ({
                value: s.serviceId,
                label: `${s.serviceName || s.service?.name || 'Serviço'} (inativo)`,
            })),
    ];
    const selectedServiceIds = services.map((s) => s.serviceId).filter(Boolean);

    const updateService = (index: number, patch: Partial<PlanoServiceItem>) => {
        setServices((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)));
    };

    const validate = () => {
        if (!name.trim()) return 'Informe o nome do plano.';

        const valor = Number(price);
        if (!valor || valor <= 0) return 'Informe um valor válido para o plano.';

        if (!services.some((s) => s.serviceId)) return 'Adicione pelo menos um serviço ao plano.';

        if (services.some((s) => s.serviceId && !(Number(s.quantity) >= 1))) {
            return 'Informe ao menos 1 uso para cada serviço.';
        }

        if (allowAgendamentoDiscountField) {
            const desconto = parsePercentInput(agendamentoDescontoPercentualStr);
            if (!Number.isFinite(desconto) || desconto < 0 || desconto > 100) {
                return 'Informe um desconto de agendamento entre 0 e 100%.';
            }
        }

        if (isSingle && !(Number(singleValidityValue) > 0)) {
            return 'Informe por quanto tempo o plano de pagamento único será válido (ex.: 12 meses).';
        }

        if (!isSingle && !indeterminateQuantity && !(Number(quantity) > 0)) {
            return 'Informe a quantidade de cobranças ou ative "Até cancelar".';
        }

        return '';
    };

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const validationError = validate();
        if (validationError) {
            errorSnackbar(validationError);
            return;
        }

        setDisabledButton(true);

        try {
            // billingType derivado da periodicity
            const billingType: BillingType = isSingle
                ? 'UNICO'
                : 'RECORRENTE';

            const base: any = {
                name: name.trim(),
                description,
                price: Number(price),
                // Normaliza serviços: remove linhas sem serviceId
                services: services
                    .filter((s) => s.serviceId)
                    .map((s) => ({
                        serviceId: s.serviceId,
                        recurrence: s.recurrence,
                        quantity: Number(s.quantity),
                    })),
                status: isActive ? 1 : 0,
                billingType,
                periodicity,
                indeterminateQuantity,
                imagesIds,
                allowSelfSubscription,
            };

            if (allowAgendamentoDiscountField) {
                base.agendamentoDescontoPercentual =
                    Math.round(parsePercentInput(agendamentoDescontoPercentualStr) * 100) / 100;
            }

            if (!isSingle && !indeterminateQuantity) {
                base.quantity = Number(quantity);
            }

            // 🔹 Para planos de pagamento único, enviamos a validade padrão
            if (isSingle) {
                base.singleValidityValue = Number(singleValidityValue);
                base.singleValidityUnit = singleValidityUnit; // 'day' | 'month'
            }

            if (id) {
                await service.update(id, base);
                successSnackbar('Plano editado com sucesso!');
            } else {
                await service.create(base);
                successSnackbar('Plano criado com sucesso!');
            }

            await reloadData();
            clear();
        } catch (e: any) {
            // Mantem o formulario aberto para nao perder o que foi preenchido.
            console.error(e);
            errorSnackbar(e?.response?.data?.message || 'Ocorreu um erro ao salvar o plano.');
        } finally {
            setDisabledButton(false);
        }
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            await service.delete(id);
            await reloadData();
            successSnackbar('Plano removido com sucesso!');
            clear();
        } catch (e) {
            errorSnackbar('Ocorreu um erro ao remover o plano.');
            setDeleting(false);
            setOpenDelete(false);
        }
    };

    if (id && loading) {
        return (
            <div className="space-y-3 px-2" aria-busy="true">
                <div className="sf-skeleton h-40" />
                <div className="sf-skeleton h-32" />
            </div>
        );
    }

    if (id && !doc) {
        return (
            <div className="sf-state">
                <Layers aria-hidden />
                <h2>Plano não encontrado</h2>
                <p>Ele pode ter sido removido.</p>
                <button type="button" className="sf-link" onClick={clear}>
                    Voltar para planos
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="estab-settings pla-page px-2">
            <div className="pla-form-grid">
                <div className="pla-form-column">
                    <SettingsSection title="Plano">
                        <SettingsRow label="Nome" htmlFor="plano-name">
                            <input
                                id="plano-name"
                                type="text"
                                className="input estab-field"
                                placeholder="Ex.: Plano Trimestral Premium"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </SettingsRow>
                        <SettingsRow label="Valor">
                            <PriceInput
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                placeholder="R$ 0,00"
                                label=""
                                className="estab-field pla-price-input"
                            />
                        </SettingsRow>
                        {allowAgendamentoDiscountField && (
                            <SettingsRow
                                label="Desconto no agendamento"
                                htmlFor="plano-discount"
                                description="Padrão em %, para novas assinaturas."
                            >
                                <input
                                    id="plano-discount"
                                    type="number"
                                    className="input estab-field"
                                    min={0}
                                    max={100}
                                    step="0.01"
                                    value={agendamentoDescontoPercentualStr}
                                    onChange={(e) => setAgendamentoDescontoPercentualStr(e.target.value)}
                                    placeholder="0"
                                />
                            </SettingsRow>
                        )}
                        <SettingsRow label="Descrição" htmlFor="plano-description" stacked>
                            <textarea
                                id="plano-description"
                                className="textarea estab-field !ml-0 !min-h-[5.5rem] !w-full"
                                placeholder="Benefícios e regras do plano"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </SettingsRow>
                    </SettingsSection>

                    <SettingsSection
                        title="Cobrança"
                        footer={
                            <span className="pla-summary">
                                {formatPlanPrice(Number(price))}
                                {getPlanPriceSuffix(periodicity)} · {getPlanBillingLabel({
                                    periodicity,
                                    quantity: Number(quantity),
                                    indeterminateQuantity,
                                    singleValidityValue: Number(singleValidityValue),
                                    singleValidityUnit,
                                })}
                            </span>
                        }
                    >
                        <SettingsRow label="Periodicidade" htmlFor="plano-periodicity">
                            <select
                                id="plano-periodicity"
                                className="select estab-field"
                                value={periodicity}
                                onChange={(e) => setPeriodicity(e.target.value as PlanPeriodicity)}
                            >
                                <option value="single">Pagamento único</option>
                                <option value="weekly">Semanal</option>
                                <option value="biweekly">Quinzenal</option>
                                <option value="monthly">Mensal</option>
                                <option value="bimonthly">Bimestral</option>
                                <option value="quarterly">Trimestral</option>
                                <option value="biannual">Semestral</option>
                                <option value="yearly">Anual</option>
                            </select>
                        </SettingsRow>

                        {isSingle ? (
                            <SettingsRow label="Validade" htmlFor="plano-validity">
                                <div className="pla-validity">
                                    <input
                                        id="plano-validity"
                                        type="number"
                                        min={1}
                                        className="input estab-field"
                                        value={singleValidityValue}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setSingleValidityValue(v === '' ? '' : Number(v));
                                        }}
                                    />
                                    <select
                                        className="select estab-field"
                                        aria-label="Unidade da validade"
                                        value={singleValidityUnit}
                                        onChange={(e) => setSingleValidityUnit(e.target.value as SingleValidityUnit)}
                                    >
                                        <option value="day">Dias</option>
                                        <option value="month">Meses</option>
                                    </select>
                                </div>
                            </SettingsRow>
                        ) : (
                            <>
                                <SettingsRow
                                    label="Até cancelar"
                                    htmlFor="plano-indeterminate"
                                    description="Sem limite de cobranças."
                                    inline
                                >
                                    <input
                                        id="plano-indeterminate"
                                        type="checkbox"
                                        className="toggle toggle-success"
                                        checked={indeterminateQuantity}
                                        onChange={(e) => setIndeterminateQuantity(e.target.checked)}
                                    />
                                </SettingsRow>
                                {!indeterminateQuantity && (
                                    <SettingsRow label="Cobranças" htmlFor="plano-quantity">
                                        <input
                                            id="plano-quantity"
                                            type="number"
                                            min={1}
                                            className="input estab-field"
                                            value={quantity}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setQuantity(v === '' ? '' : Number(v));
                                            }}
                                        />
                                    </SettingsRow>
                                )}
                            </>
                        )}
                    </SettingsSection>
                </div>

                <div className="pla-form-column">
                    <SettingsSection
                        title="Serviços incluídos"
                        footer="Quantas vezes cada serviço pode ser usado por período."
                    >
                        {services.map((item, index) => (
                            <SettingsRow key={index} stacked>
                                <div className="pla-service">
                                    <select
                                        className="select estab-field pla-service-select"
                                        aria-label={`Serviço ${index + 1}`}
                                        value={item.serviceId}
                                        onChange={(e) => updateService(index, { serviceId: e.target.value })}
                                    >
                                        <option value="">Selecione um serviço</option>
                                        {allServiceOptions.map(({ label, value }) => (
                                            <option
                                                key={value}
                                                value={value}
                                                disabled={value !== item.serviceId && selectedServiceIds.includes(value)}
                                            >
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="pla-service-limit">
                                        <input
                                            type="number"
                                            min={1}
                                            className="input estab-field"
                                            aria-label="Quantidade de usos"
                                            value={item.quantity}
                                            onChange={(e) => updateService(index, { quantity: Number(e.target.value) })}
                                        />
                                        <span>vez(es)</span>
                                        <select
                                            className="select estab-field"
                                            aria-label="Período de uso"
                                            value={item.recurrence}
                                            onChange={(e) =>
                                                updateService(index, { recurrence: e.target.value as ServiceRecurrence })
                                            }
                                        >
                                            <option value="semanal">por semana</option>
                                            <option value="mensal">por mês</option>
                                        </select>
                                    </div>
                                    <button
                                        type="button"
                                        className="pla-service-remove"
                                        aria-label={`Remover serviço ${index + 1}`}
                                        onClick={() => setServices((current) => current.filter((_, i) => i !== index))}
                                    >
                                        <Trash2 aria-hidden />
                                    </button>
                                </div>
                            </SettingsRow>
                        ))}
                        <SettingsRow inline>
                            <button
                                type="button"
                                className="estab-link-button justify-self-start"
                                disabled={selectedServiceIds.length >= allServiceOptions.length && services.length > 0}
                                onClick={() =>
                                    setServices((current) => [
                                        ...current,
                                        { serviceId: '', recurrence: 'mensal', quantity: 1 },
                                    ])
                                }
                            >
                                <Plus aria-hidden />
                                Adicionar serviço
                            </button>
                        </SettingsRow>
                    </SettingsSection>

                    <SettingsSection title="Imagens" footer="Até 5 imagens. A primeira aparece como capa do plano.">
                        <FormPlanoImages
                            value={imagesIds}
                            setValue={setImagesIds}
                            disabled={disabledButton}
                            onUploadingChange={setUploadingImages}
                        />
                    </SettingsSection>

                    <SettingsSection title="Disponibilidade">
                        <SettingsRow label="Plano ativo" htmlFor="plano-active" inline>
                            <input
                                id="plano-active"
                                type="checkbox"
                                className="toggle toggle-success"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                            />
                        </SettingsRow>
                        <SettingsRow
                            label="Cliente pode contratar sozinho"
                            htmlFor="plano-self-subscription"
                            description="Desative se só a equipe puder habilitar este plano."
                            inline
                        >
                            <input
                                id="plano-self-subscription"
                                type="checkbox"
                                className="toggle toggle-success"
                                checked={allowSelfSubscription}
                                onChange={(e) => setAllowSelfSubscription(e.target.checked)}
                            />
                        </SettingsRow>
                    </SettingsSection>

                    {!!id && (
                        <div className="estab-group">
                            <button type="button" className="sf-destructive-row" onClick={() => setOpenDelete(true)}>
                                Remover plano
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="estab-savebar mt-6 gap-2">
                <button
                    type="button"
                    className="btn btn-ghost flex-1 sm:flex-none sm:min-w-[8rem]"
                    onClick={clear}
                    disabled={disabledButton}
                >
                    Cancelar
                </button>
                <button
                    className="btn btn-primary flex-1 sm:flex-none sm:min-w-[11rem]"
                    disabled={disabledButton || uploadingImages}
                    type="submit"
                >
                    {disabledButton && <span className="loading loading-spinner loading-sm"></span>}
                    {id ? 'Salvar alterações' : 'Criar plano'}
                </button>
            </div>

            {openDelete && (
                <Modal title="Remover plano?" onClose={() => setOpenDelete(false)} desktopClassName="sm:!max-w-md">
                    <p className="text-sm text-base-content/70">
                        O plano sai da lista e não pode mais ser contratado. As assinaturas existentes continuam valendo.
                    </p>
                    <div className="mt-5 flex justify-end gap-2">
                        <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => setOpenDelete(false)}
                            disabled={deleting}
                        >
                            Cancelar
                        </button>
                        <button type="button" className="btn btn-error" onClick={handleDelete} disabled={deleting}>
                            {deleting && <span className="loading loading-spinner loading-sm"></span>}
                            Remover
                        </button>
                    </div>
                </Modal>
            )}
        </form>
    );
}
